import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  UserGroupIcon, ShieldCheckIcon, KeyIcon, Cog6ToothIcon, 
  ChartBarIcon, DocumentTextIcon, UsersIcon, UserPlusIcon 
} from '@heroicons/react/24/outline';
import { Card, Stats, Badge, Loading } from '../../components/Common';
import api from '../../services/api';

const AdminDashboard = () => {
  useSelector((state) => state.auth); // Initialize auth state
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalAdmins: 0,
    totalOperators: 0,
    activeRoles: 0,
    usersByRole: []
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAdminStats();
  }, []);

  const fetchAdminStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch dashboard data
      const dashboardRes = await api.get('/admin/dashboard');
      const dashboardData = dashboardRes.data?.data || {};

      // Fetch roles
      const rolesRes = await api.get('/auth/roles').catch(() => ({ data: { data: [] } }));
      const roles = rolesRes.data?.data || [];

      // Fetch all users to count by role
      const usersRes = await api.get('/admin/users/all').catch(() => ({ data: { data: [] } }));
      const users = usersRes.data?.data || [];

      // Count users by role
      const usersByRole = roles.map(role => {
        const count = users.filter(u => u.role === role.code || u.roleCode === role.code).length;
        return { name: role.name, code: role.code, count };
      }).filter(r => r.count > 0);

      setStats({
        totalUsers: dashboardData.overview?.totalUsers || users.length || 0,
        totalAdmins: dashboardData.overview?.totalAdmins || users.filter(u => u.role === 'ADMIN' || u.roleCode === 'ADMIN').length || 0,
        totalOperators: dashboardData.overview?.totalOperators || users.length || 0,
        activeRoles: roles.filter(r => r.is_enable).length || 0,
        usersByRole
      });

      // Set recent activity
      setRecentActivity(dashboardData.recentActivity?.filter(a => a.type === 'user') || []);

    } catch (err) {
      console.error('Admin dashboard error:', err);
      setError('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };

  const adminModules = [
    { name: 'User Management', description: 'Manage all users and their roles', icon: UserGroupIcon, path: '/admin/users', color: 'bg-indigo-100 text-indigo-600' },
    { name: 'Role Management', description: 'Configure system roles', icon: ShieldCheckIcon, path: '/admin/users', color: 'bg-purple-100 text-purple-600' },
    { name: 'Permissions', description: 'Manage access controls', icon: KeyIcon, path: '/admin/users', color: 'bg-cyan-100 text-cyan-600' },
    { name: 'System Settings', description: 'Configure system preferences', icon: Cog6ToothIcon, path: '/settings', color: 'bg-emerald-100 text-emerald-600' },
    { name: 'Analytics', description: 'View platform analytics', icon: ChartBarIcon, path: '/analytics', color: 'bg-amber-100 text-amber-600' },
    { name: 'Reports', description: 'Generate and view reports', icon: DocumentTextIcon, path: '/reports', color: 'bg-rose-100 text-rose-600' },
  ];

  if (loading) return <Loading fullScreen text="Loading admin dashboard..." />;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Admin Dashboard</h1>
        <p className="text-slate-500">System administration and user management (Unified Operator System)</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Stats - All users from Operator table */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stats 
          title="Total Users" 
          value={stats.totalUsers} 
          icon={UserGroupIcon} 
          iconBg="bg-indigo-100" 
          iconColor="text-indigo-600"
          subtitle="All users in system"
        />
        <Stats 
          title="Admin Users" 
          value={stats.totalAdmins} 
          icon={ShieldCheckIcon} 
          iconBg="bg-purple-100" 
          iconColor="text-purple-600"
          subtitle="Users with ADMIN role"
        />
        <Stats 
          title="Active Roles" 
          value={stats.activeRoles} 
          icon={KeyIcon} 
          iconBg="bg-cyan-100" 
          iconColor="text-cyan-600"
          subtitle="Enabled role types"
        />
        <Stats 
          title="System Health" 
          value="99.9%" 
          icon={Cog6ToothIcon} 
          iconBg="bg-emerald-100" 
          iconColor="text-emerald-600"
          subtitle="Uptime"
        />
      </div>

      {/* Users by Role */}
      {stats.usersByRole.length > 0 && (
        <Card title="Users by Role" subtitle="Distribution of users across roles">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {stats.usersByRole.map((role) => (
              <div key={role.code} className="bg-slate-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-slate-800">{role.count}</div>
                <div className="text-sm text-slate-500">{role.name}</div>
                <Badge variant={role.code === 'ADMIN' ? 'primary' : 'secondary'} className="mt-2">
                  {role.code}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Admin Modules */}
      <Card title="Admin Modules" subtitle="Quick access to administration features">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {adminModules.map((module) => (
            <Link key={module.name} to={module.path} className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${module.color}`}>
                  <module.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 group-hover:text-indigo-600 transition-colors">{module.name}</h3>
                  <p className="text-sm text-slate-500">{module.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Card>

      {/* System Info */}
      <Card title="System Information" subtitle="Unified user management system">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <UsersIcon className="w-6 h-6 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-800">Unified Operator Table</h4>
              <p className="text-sm text-blue-700 mt-1">
                All users (including admins) are stored in the <code className="bg-blue-100 px-1 rounded">operator</code> table. 
                User type is determined by their role assignment in <code className="bg-blue-100 px-1 rounded">entity_operator_role_mapping</code>.
              </p>
              <ul className="text-sm text-blue-600 mt-2 space-y-1">
                <li>• <strong>Admin users:</strong> Operators with ADMIN role</li>
                <li>• <strong>Content Managers:</strong> Operators with CONTENT_MANAGER role</li>
                <li>• <strong>Editors:</strong> Operators with EDITOR role</li>
                <li>• <strong>Writers:</strong> Operators with CONTENT_WRITER role</li>
                <li>• <strong>Viewers:</strong> Operators with VIEWER role (no portal access)</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* Recent User Activity */}
      <Card title="Recent User Activity" subtitle="Latest user-related actions" noPadding>
        <div className="divide-y divide-slate-100">
          {recentActivity.length > 0 ? (
            recentActivity.slice(0, 5).map((activity, index) => (
              <div key={activity.id || index} className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <UserPlusIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <span className="font-medium text-slate-800">{activity.title}</span>
                    <p className="text-sm text-slate-500">{activity.description}</p>
                  </div>
                </div>
                <span className="text-sm text-slate-400">
                  {activity.timestamp ? new Date(activity.timestamp).toLocaleDateString() : 'Recently'}
                </span>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-slate-500">
              <UserGroupIcon className="w-12 h-12 mx-auto text-slate-300 mb-2" />
              <p>No recent user activity</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default AdminDashboard;
